export const PROGRESS_SCHEMA_VERSION = 3 as const;
export const PROGRESS_MAX_BYTES = 16 * 1024;
export const MAX_REVISION = 1_000_000_000;
export const MAX_SCORE = 1_000_000_000;
export const MAX_LENGTH = 1_000_000;
export const MAX_MATCHES = 1_000_000;
export const MAX_ELIMINATIONS = 10_000_000;
export const MAX_FOOD = 1_000_000_000;
export const MAX_TOTAL = 1_000_000_000_000;
export const MAX_CURRENCY = 1_000_000_000;
export const MAX_TUTORIAL_STEP = 5;

export const COSMETIC_IDS = [
  "gloam",
  "coral",
  "lime",
  "constellation",
  "indigo-tide",
  "jade-circuit",
] as const;
export type CosmeticId = (typeof COSMETIC_IDS)[number];
export type PerformancePreference = "auto" | "quality" | "low";
export type ThemePreference = "dark" | "light";

export interface ProgressRecords {
  bestScore: number;
  longestLength: number;
  longestSurvivalMs: number;
  matchesPlayed: number;
  eliminations: number;
  foodCollected: number;
  totalScore: number;
  totalSurvivalMs: number;
  endlessSessions: number;
  endlessDeaths: number;
  longestEndlessSessionMs: number;
  longestEndlessLength: number;
  endlessEliminations: number;
  endlessFoodCollected: number;
  totalEndlessSessionMs: number;
  totalEndlessAliveMs: number;
}

export interface ProgressSettings {
  music: boolean;
  sfx: boolean;
  vibration: boolean;
  reducedMotion: boolean;
  reducedParticles: boolean;
  performance: PerformancePreference;
  theme: ThemePreference;
  skipReadyCountdown: boolean;
}

export interface TutorialProgress {
  completed: boolean;
  step: number;
}

export type OwnedCosmetics = Partial<Record<CosmeticId, true>> & {
  gloam: true;
};

export interface Progress {
  schemaVersion: typeof PROGRESS_SCHEMA_VERSION;
  revision: number;
  updatedAt: number;
  records: ProgressRecords;
  settings: ProgressSettings;
  tutorial: TutorialProgress;
  currency: number;
  ownedCosmetics: OwnedCosmetics;
  equippedCosmetic: CosmeticId;
}

export type ProgressValidation =
  { ok: true; value: Progress } | { ok: false; reason: string };

const TOP_LEVEL_FIELDS = new Set([
  "schemaVersion",
  "revision",
  "updatedAt",
  "records",
  "settings",
  "tutorial",
  "currency",
  "ownedCosmetics",
  "equippedCosmetic",
]);
const RECORD_FIELDS = new Set([
  "bestScore",
  "longestLength",
  "longestSurvivalMs",
  "matchesPlayed",
  "eliminations",
  "foodCollected",
  "totalScore",
  "totalSurvivalMs",
  "endlessSessions",
  "endlessDeaths",
  "longestEndlessSessionMs",
  "longestEndlessLength",
  "endlessEliminations",
  "endlessFoodCollected",
  "totalEndlessSessionMs",
  "totalEndlessAliveMs",
]);
const SETTING_FIELDS = new Set([
  "music",
  "sfx",
  "vibration",
  "reducedMotion",
  "reducedParticles",
  "performance",
  "theme",
  "skipReadyCountdown",
]);
const TUTORIAL_FIELDS = new Set(["completed", "step"]);
const COSMETIC_ID_SET = new Set<string>(COSMETIC_IDS);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasExactFields = (
  value: Record<string, unknown>,
  fields: Set<string>,
): boolean => {
  const keys = Object.keys(value);
  return keys.length === fields.size && keys.every((key) => fields.has(key));
};

const integerBetween = (
  value: unknown,
  min: number,
  max: number,
): value is number =>
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value >= min &&
  value <= max;

const byteLength = (value: string): number =>
  new TextEncoder().encode(value).byteLength;

export const createDefaultProgress = (now = 0): Progress => ({
  schemaVersion: PROGRESS_SCHEMA_VERSION,
  revision: 0,
  updatedAt: now,
  records: {
    bestScore: 0,
    longestLength: 0,
    longestSurvivalMs: 0,
    matchesPlayed: 0,
    eliminations: 0,
    foodCollected: 0,
    totalScore: 0,
    totalSurvivalMs: 0,
    endlessSessions: 0,
    endlessDeaths: 0,
    longestEndlessSessionMs: 0,
    longestEndlessLength: 0,
    endlessEliminations: 0,
    endlessFoodCollected: 0,
    totalEndlessSessionMs: 0,
    totalEndlessAliveMs: 0,
  },
  settings: {
    music: true,
    sfx: true,
    vibration: true,
    reducedMotion: false,
    reducedParticles: false,
    performance: "auto",
    theme: "dark",
    skipReadyCountdown: false,
  },
  tutorial: { completed: false, step: 0 },
  currency: 0,
  ownedCosmetics: { gloam: true },
  equippedCosmetic: "gloam",
});

export const cloneProgress = (progress: Progress): Progress => ({
  ...progress,
  records: { ...progress.records },
  settings: { ...progress.settings },
  tutorial: { ...progress.tutorial },
  ownedCosmetics: { ...progress.ownedCosmetics },
});

export const serializedProgressBytes = (value: unknown): number => {
  try {
    return byteLength(
      typeof value === "string" ? value : JSON.stringify(value),
    );
  } catch {
    return Number.POSITIVE_INFINITY;
  }
};

export function validateProgress(input: unknown): ProgressValidation {
  if (serializedProgressBytes(input) > PROGRESS_MAX_BYTES) {
    return {
      ok: false,
      reason: "progress exceeds the maximum serialized size",
    };
  }
  if (!isRecord(input) || !hasExactFields(input, TOP_LEVEL_FIELDS)) {
    return { ok: false, reason: "progress must contain the exact V3 fields" };
  }
  if (input.schemaVersion !== PROGRESS_SCHEMA_VERSION) {
    return { ok: false, reason: "unsupported progress schema version" };
  }
  if (!integerBetween(input.revision, 0, MAX_REVISION)) {
    return { ok: false, reason: "revision is invalid" };
  }
  if (!integerBetween(input.updatedAt, 0, Number.MAX_SAFE_INTEGER)) {
    return { ok: false, reason: "updatedAt is invalid" };
  }

  const records = input.records;
  if (!isRecord(records) || !hasExactFields(records, RECORD_FIELDS)) {
    return { ok: false, reason: "records must contain the exact V3 fields" };
  }
  const recordLimits: Record<keyof ProgressRecords, number> = {
    bestScore: MAX_SCORE,
    longestLength: MAX_LENGTH,
    longestSurvivalMs: MAX_TOTAL,
    matchesPlayed: MAX_MATCHES,
    eliminations: MAX_ELIMINATIONS,
    foodCollected: MAX_FOOD,
    totalScore: MAX_TOTAL,
    totalSurvivalMs: MAX_TOTAL,
    endlessSessions: MAX_MATCHES,
    endlessDeaths: MAX_ELIMINATIONS,
    longestEndlessSessionMs: MAX_TOTAL,
    longestEndlessLength: MAX_LENGTH,
    endlessEliminations: MAX_ELIMINATIONS,
    endlessFoodCollected: MAX_FOOD,
    totalEndlessSessionMs: MAX_TOTAL,
    totalEndlessAliveMs: MAX_TOTAL,
  };
  for (const [key, max] of Object.entries(recordLimits) as Array<
    [keyof ProgressRecords, number]
  >) {
    if (!integerBetween(records[key], 0, max)) {
      return { ok: false, reason: `${key} is invalid` };
    }
  }

  const settings = input.settings;
  if (!isRecord(settings) || !hasExactFields(settings, SETTING_FIELDS)) {
    return { ok: false, reason: "settings must contain the exact V3 fields" };
  }
  for (const key of [
    "music",
    "sfx",
    "vibration",
    "reducedMotion",
    "reducedParticles",
    "skipReadyCountdown",
  ] as const) {
    if (typeof settings[key] !== "boolean") {
      return { ok: false, reason: `${key} must be boolean` };
    }
  }
  if (
    settings.performance !== "auto" &&
    settings.performance !== "quality" &&
    settings.performance !== "low"
  ) {
    return { ok: false, reason: "performance is invalid" };
  }
  if (settings.theme !== "dark" && settings.theme !== "light") {
    return { ok: false, reason: "theme is invalid" };
  }

  const tutorial = input.tutorial;
  if (!isRecord(tutorial) || !hasExactFields(tutorial, TUTORIAL_FIELDS)) {
    return { ok: false, reason: "tutorial must contain the exact V1 fields" };
  }
  if (
    typeof tutorial.completed !== "boolean" ||
    !integerBetween(tutorial.step, 0, MAX_TUTORIAL_STEP)
  ) {
    return { ok: false, reason: "tutorial is invalid" };
  }
  if (tutorial.completed !== (tutorial.step === MAX_TUTORIAL_STEP)) {
    return {
      ok: false,
      reason: "tutorial completion and step are inconsistent",
    };
  }

  if (!integerBetween(input.currency, 0, MAX_CURRENCY)) {
    return { ok: false, reason: "currency is invalid" };
  }
  const owned = input.ownedCosmetics;
  if (
    !isRecord(owned) ||
    owned.gloam !== true ||
    Object.keys(owned).length > COSMETIC_IDS.length
  ) {
    return {
      ok: false,
      reason: "ownedCosmetics must include the default cosmetic",
    };
  }
  if (
    Object.entries(owned).some(
      ([key, value]) => !COSMETIC_ID_SET.has(key) || value !== true,
    )
  ) {
    return { ok: false, reason: "ownedCosmetics contains an invalid entry" };
  }
  if (
    typeof input.equippedCosmetic !== "string" ||
    !COSMETIC_ID_SET.has(input.equippedCosmetic) ||
    owned[input.equippedCosmetic] !== true
  ) {
    return { ok: false, reason: "equippedCosmetic must be owned" };
  }
  return { ok: true, value: input as unknown as Progress };
}
